import { Dispatch, SetStateAction, useCallback, useContext, useMemo } from "react";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress } from "@mui/material";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { DropdownSecondaryCodeRequirements, IOptionSecondaryCodes } from "../Dropdown";
import { useTranslation } from "react-i18next";
import { IEditionSecondaryCodes } from "../../hooks/useSecondaryCodeRequirementsColumnsInterface";
import { IEventReportRow } from "../../SecondaryCodeRequerimentsInterfaces";
import { handleNameTranslation } from "utilities/localesUtilities";

export const AreasDropdown = ({
    row,
    edit,
    setEdit,
}: {
    edit: IEditionSecondaryCodes[];
    setEdit: Dispatch<SetStateAction<IEditionSecondaryCodes[]>>;
    row: IEventReportRow;
}) => {
    const { user_id } = useContext(UserContext);
    const { t, i18n } = useTranslation();

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFailureTypeId = useMemo(
        () => edit[existingEdition]?.changes?.failure_type_id,
        [edit, existingEdition],
    );
    const selectedAreaId = useMemo(() => edit[existingEdition]?.changes?.area_id, [edit, existingEdition]);

    const { loading, areas } = EventReportsEntity.GetAreas(row.machine_center.machine_center_id, selectedFailureTypeId);

    const areaOptions: IOptionSecondaryCodes[] = useMemo(
        () =>
            areas.map((a) => ({
                name: handleNameTranslation(i18n.language, a.translation.translation_names),
                id: a.area_id,
            })),
        [areas, i18n.language],
    );

    const handleAreaUpdate = useCallback(
        async (id: number) => {
            if (existingEdition !== -1) {
                setEdit((prevEdit) => {
                    const newEdit = [...prevEdit];
                    newEdit[existingEdition] = {
                        ...newEdit[existingEdition],
                        changes: {
                            ...newEdit[existingEdition]?.changes,
                            area_id: id,
                            functional_location_id: undefined,
                            failure_code_id: undefined,
                            secondary_failure_code_id: undefined,
                            ...(user_id && { edited_by_user_id: user_id }),
                        },
                    };
                    return newEdit;
                });
            }
        },
        [existingEdition, setEdit, user_id],
    );

    return loading ? (
        <CircularProgress size={20} color="info" />
    ) : selectedFailureTypeId && edit[existingEdition]?.location_specific ? (
        <DropdownSecondaryCodeRequirements
            obligatory
            inlineStyle={{ width: "80px" }}
            optionSelected={selectedAreaId}
            options={areaOptions}
            handleUpdate={handleAreaUpdate}
        />
    ) : (
        <span>{t("locations.mill_dashboards.details.not_applicable")}</span>
    );
};
